export interface ImportMap {
  imports: {
    [key: string]: string;
  };
}

export interface NavigationSubItem {
  key: string;
  iconPath: string;
  url: string;
  label: string;
}

export interface NavigationItem {
  key: string;
  iconPath: string;
  label: string;
  url: string;
  priority?: number;
  subItems?: NavigationSubItem[];
}

export interface NavigationConfig {
  navigationItems: NavigationItem[];
  translations?: Record<string, Record<string, string>>; // market -> key/value translations
}

export interface AppInfo {
  name: string;
  url: string;
  navigationItems?: NavigationItem[];
  translations?: Record<string, Record<string, string>>; // market -> key/value translations
}

// =============================================================================
// CONFIG
// =============================================================================

const APP_PREFIX = '@ionos/';
const NAMESPACE = 'IONOS_MARKETING_HUB';

// =============================================================================
// HELPERS
// =============================================================================

function getImportMapUrl(): string {
  const host = globalThis.location.hostname;

  if (host === 'localhost' || host.startsWith('dev-')) {
    return '/importmap.dev.json';
  }

  if (host.endsWith('server.lan')) {
    return '/importmap.prod.json';
  }

  return '/importmap.pulse.json';
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const response = await fetch(url);
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

function extractAppUrl(fullUrl: string): string {
  const currentProtocol = globalThis.location.protocol;
  const normalized = fullUrl.startsWith('//') ? `${currentProtocol}${fullUrl}` : fullUrl;

  const { protocol, host, pathname } = new URL(normalized);
  const pathWithoutFile = pathname.split('/').slice(0, -1).join('/');

  return `${protocol}//${host}${pathWithoutFile || '/'}`;
}

// =============================================================================
// MAIN LOGIC
// =============================================================================

async function loadImportMaps(): Promise<ImportMap> {
  const host = globalThis.location.hostname;
  const mainUrl = getImportMapUrl();

  // Load main import map (with fallback)
  let importMap = await fetchJson<ImportMap>(mainUrl);
  if (!importMap) {
    importMap = await fetchJson<ImportMap>('/importmap.json');
  }

  if (!importMap) {
    return { imports: {} };
  }

  // On localhost, merge local overrides if they exist
  if (host === 'localhost') {
    const localMap = await fetchJson<ImportMap>('/importmap.local.json');
    if (localMap) {
      importMap = {
        imports: {
          ...importMap.imports,
          ...localMap.imports,
        },
      };
    }
  }

  return importMap;
}

function extractApps(importMap: ImportMap): Array<AppInfo> {
  const { imports } = importMap;

  return Object.entries(imports)
    .filter(([key]) => key.startsWith(APP_PREFIX))
    .map(([name, url]) => {
      const cleanName = name.replace(APP_PREFIX, '').replace(/^\//u, '');

      return {
        name: cleanName,
        url: extractAppUrl(url),
      };
    });
}

async function loadNavigationForApp(app: AppInfo): Promise<AppInfo> {
  const navigationUrl = `${app.url}/navigation.json`;
  const navConfig = await fetchJson<NavigationConfig>(navigationUrl);

  if (navConfig?.navigationItems) {
    return {
      ...app,
      navigationItems: navConfig.navigationItems,
      translations: navConfig.translations,
    };
  }

  return app;
}

async function loadAllNavigations(apps: Array<AppInfo>): Promise<Array<AppInfo>> {
  // Load navigation.json for all apps in parallel
  const appsWithNavigation = await Promise.all(apps.map((app) => loadNavigationForApp(app)));

  return appsWithNavigation;
}

function registerAppsGlobally(apps: Array<AppInfo>): void {
  window[NAMESPACE] = window[NAMESPACE] || [];
  window[NAMESPACE].apps = apps;
}

async function startApp(): Promise<void> {
  // 1. Load import maps (main + local override)
  const importMap = await loadImportMaps();

  // 2. Extract app info from import map
  const apps = extractApps(importMap);

  // 3. Load navigation.json for each app (parallel!)
  const appsWithNavigation = await loadAllNavigations(apps);

  // 4. Register apps in global namespace
  registerAppsGlobally(appsWithNavigation);

  // 5. Add import map to SystemJS
  System.addImportMap(importMap);

  // 6. Start main app
  await import('./ionos-marketing-hub');
}

// =============================================================================
// START
// =============================================================================

startApp();
